import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ExternalLink, FileText, MessagesSquare, Send, XCircle } from 'lucide-react';

import { useSession } from '../../../app/useSession';
import { cn } from '../../../lib/cn';
import { useToast } from '../../../components/Toast';
import { QuoteWizard } from '../../quotes';
import {
  useAppendLeadNote,
  useChangeLeadStatus,
  useConvertLeadToCustomer,
  useLifecycleLead,
} from '../botQueries';
import {
  LIFECYCLE_LEAD_STATUS_LABELS_HE,
  type LifecycleLeadStatus,
} from '../bot.schema';
import styles from './LeadLifecycleCard.module.css';

interface LeadLifecycleCardProps {
  orgId: string | null;
  leadId: string | null;
}

const STATUS_CLASS: Record<LifecycleLeadStatus, string | undefined> = {
  new: styles.statusNew,
  contacted: styles.statusContacted,
  qualified: styles.statusQualified,
  quoted: styles.statusQuoted,
  converted: styles.statusConverted,
  disqualified: styles.statusDisqualified,
};

export function LeadLifecycleCard({ orgId, leadId }: LeadLifecycleCardProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const userId = session.status === 'signed-in' ? session.session.user.id : null;
  const leadQ = useLifecycleLead(orgId, leadId);

  const changeStatus = useChangeLeadStatus(orgId);
  const convertToCustomer = useConvertLeadToCustomer(orgId);
  const appendNote = useAppendLeadNote(orgId);

  const [note, setNote] = useState('');
  const [quoteOpen, setQuoteOpen] = useState(false);

  function handleQuoteCreated() {
    setQuoteOpen(false);
    toast.success('הצעת המחיר נשמרה והליד סומן כ«הוצעה»');
    void qc.invalidateQueries({ queryKey: ['lifecycle-lead', orgId, leadId] });
    void qc.invalidateQueries({ queryKey: ['leads', orgId] });
    void qc.invalidateQueries({ queryKey: ['quotes', orgId] });
  }

  if (!leadId) {
    return (
      <div className={styles.card}>
        <div className={styles.head}>
          <span className={styles.title}>ליד מקושר</span>
        </div>
        <div className={styles.emptyState}>
          <span>השיחה לא קושרה לליד.</span>
          <Link to="/leads" className={styles.actionBtn}>
            פתח לידים
          </Link>
        </div>
      </div>
    );
  }

  if (leadQ.isLoading) {
    return (
      <div className={styles.card}>
        <div className={styles.head}>
          <span className={styles.title}>ליד מקושר</span>
        </div>
        <p className={styles.emptyState}>טוען…</p>
      </div>
    );
  }

  const lead = leadQ.data;
  if (!lead) {
    return (
      <div className={styles.card}>
        <div className={styles.head}>
          <span className={styles.title}>ליד מקושר</span>
        </div>
        <p className={styles.emptyState}>הליד לא נמצא.</p>
      </div>
    );
  }

  const busy =
    changeStatus.isPending ||
    convertToCustomer.isPending ||
    appendNote.isPending;

  async function setStatus(to: LifecycleLeadStatus) {
    if (!lead) return;
    try {
      await changeStatus.mutateAsync({ lead_id: lead.id, to_status: to });
      toast.success('הסטטוס עודכן');
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  async function onConvertToCustomer() {
    if (!lead) return;
    if (!window.confirm('להמיר את הליד ללקוח? פעולה זו תיצור לקוח חדש ותסגור את הליד.')) return;
    try {
      const res = await convertToCustomer.mutateAsync({ lead_id: lead.id });
      toast.success('הליד הומר ללקוח');
      navigate(`/customers/${res.customer_id}`);
    } catch {
      toast.error('ההמרה נכשלה · נסה שוב');
    }
  }

  function onCreateQuote() {
    if (!lead) return;
    // The QuoteWizard is the single source of quote creation. If a quote already
    // exists, jump to it; otherwise open the wizard (which links + advances the lead).
    if (lead.linked_quote_id) {
      navigate(`/quotes?id=${lead.linked_quote_id}`);
      return;
    }
    setQuoteOpen(true);
  }

  async function onAppendNote() {
    if (!lead) return;
    const trimmed = note.trim();
    if (!trimmed) return;
    try {
      await appendNote.mutateAsync({ lead_id: lead.id, note: trimmed });
      setNote('');
      toast.success('ההערה נשמרה');
    } catch {
      toast.error('לא הצלחנו לשמור את ההערה · נסה שוב');
    }
  }

  const statusClass = STATUS_CLASS[lead.status] ?? styles.statusNew;

  return (
    <>
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.title}>ליד מקושר</span>
        <span className={cn(styles.statusBadge, statusClass)}>
          {LIFECYCLE_LEAD_STATUS_LABELS_HE[lead.status]}
        </span>
      </div>

      <div className={styles.leadSummary}>
        <span className={styles.leadName}>{lead.full_name}</span>
        {lead.company_name ? (
          <span className={styles.leadMeta}>{lead.company_name}</span>
        ) : null}
        {lead.interest ? (
          <span className={styles.leadMeta}>{lead.interest}</span>
        ) : null}
      </div>

      {lead.linked_customer_id ? (
        <div className={styles.linkedRow}>
          <ExternalLink size={12} aria-hidden="true" />
          <Link to={`/customers/${lead.linked_customer_id}`}>פתח את הלקוח המקושר</Link>
        </div>
      ) : null}
      {lead.linked_quote_id ? (
        <div className={styles.linkedRow}>
          <ExternalLink size={12} aria-hidden="true" />
          <Link to={`/quotes?id=${lead.linked_quote_id}`}>פתח את הצעת המחיר המקושרת</Link>
        </div>
      ) : null}

      <div className={styles.actions}>
        {lead.status === 'new' ? (
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy}
            onClick={() => { void setStatus('contacted'); }}
          >
            <MessagesSquare size={14} aria-hidden="true" />
            ענה כעת
          </button>
        ) : null}

        {lead.status === 'contacted' ? (
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy}
            onClick={() => { void setStatus('qualified'); }}
          >
            <ArrowLeftRight size={14} aria-hidden="true" />
            סמן כמוסמך
          </button>
        ) : null}

        <button
          type="button"
          className={cn(styles.actionBtn, styles.actionPrimary)}
          disabled={busy || lead.status === 'converted' || lead.status === 'disqualified'}
          onClick={onCreateQuote}
        >
          <Send size={14} aria-hidden="true" />
          {lead.linked_quote_id ? 'פתח הצעת מחיר' : 'צור הצעת מחיר'}
        </button>

        <button
          type="button"
          className={styles.actionBtn}
          disabled={busy || lead.status === 'converted' || lead.status === 'disqualified' || Boolean(lead.linked_customer_id)}
          onClick={() => { void onConvertToCustomer(); }}
        >
          <FileText size={14} aria-hidden="true" />
          המר ללקוח
        </button>

        <button
          type="button"
          className={cn(styles.actionBtn, styles.actionDanger)}
          disabled={busy || lead.status === 'disqualified'}
          onClick={() => { void setStatus('disqualified'); }}
        >
          <XCircle size={14} aria-hidden="true" />
          סגור — לא רלוונטי
        </button>
      </div>

      <div className={styles.noteWrap}>
        <label htmlFor="lifecycle-note" className={styles.leadMeta}>הערה מהירה</label>
        <textarea
          id="lifecycle-note"
          className={styles.noteTextarea}
          value={note}
          onChange={(e) => { setNote(e.target.value); }}
          placeholder="כתוב כאן הערה — תישמר בהיסטוריית הליד עם חותמת זמן"
          maxLength={2000}
        />
        <div className={styles.noteRow}>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={busy || !note.trim()}
            onClick={() => { void onAppendNote(); }}
          >
            הוסף הערה
          </button>
        </div>
      </div>
    </div>
    <QuoteWizard
      open={quoteOpen}
      orgId={orgId}
      userId={userId}
      preselectedLead={{ id: lead.id, full_name: lead.full_name, company_name: lead.company_name }}
      onClose={() => { setQuoteOpen(false); }}
      onCreated={handleQuoteCreated}
    />
    </>
  );
}
